import { ReactNode, useEffect, useMemo, useState } from 'react';

import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Button, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

interface ConfirmationModalProps {
  showModal: boolean;
  onClose?: () => void;
  onConfirm: () => void;
  icon?: ReactNode;
  title?: ReactNode | string;
  content: string;
  cancelLabel?: string;
  confirmLabel?: string;
  color?: 'primary' | 'error';
}

function ConfirmationModal({
  showModal,
  onClose,
  onConfirm,
  icon,
  title = 'Are you sure?',
  content = 'This action cannot be undone.',
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  color = 'primary',
}: ConfirmationModalProps) {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const colorMap = useMemo(
    () => ({
      primary: {
        main: theme.palette.primary.main,
        light: theme.palette.primary.light,
      },
      error: {
        main: theme.palette.error.main,
        light: theme.palette.error.light,
      },
    }),
    [theme]
  );

  const selectedColor = colorMap[color] || colorMap.primary;
  const handleModalClose = () => {
    // Always reset loading when the modal is closed by the parent
    setIsLoading(false);
    onClose && onClose();
  };

  // Reset loading whenever the visibility changes (e.g., after a completed action)
  useEffect(() => {
    if (!showModal) {
      setIsLoading(false);
    }
  }, [showModal]);

  return (
    <Dialog
      open={showModal}
      fullScreen={fullScreen}
      onClose={isLoading ? undefined : handleModalClose}
    >
      <DialogContent>
        <Stack
          display="flex"
          justifyContent="center"
          alignItems="center"
          spacing={2}
        >
          {icon && (
            <Box
              bgcolor={selectedColor.light}
              color={selectedColor.main}
              borderRadius="50%"
              padding={1}
              height="48px"
              width="48px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {icon}
            </Box>
          )}
          <Typography variant="h3" textAlign="center">
            {title}
          </Typography>
          <Box paddingX={8} textAlign="center">
            <Typography variant="body1" color={theme.palette.grey[600]}>
              {content}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box paddingX={3} display="flex" justifyContent="space-between" gap={2}>
          <Button variant="text" onClick={handleModalClose} color={color}>
            {cancelLabel}
          </Button>
          <LoadingButton
            variant="contained"
            onClick={() => {
              setIsLoading(true);
              try {
                const maybePromise: any = onConfirm();
                if (
                  maybePromise &&
                  typeof maybePromise.finally === 'function'
                ) {
                  maybePromise.finally(() => setIsLoading(false));
                }
              } catch (e) {
                setIsLoading(false);
              }
            }}
            color={color}
            loading={isLoading}
          >
            {confirmLabel}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmationModal;
